/**
 * A file to store 'lists' that are used to show things in the car hire page
 */
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import LineFileAltIcon from 'LuxKit/Icons/line/LineFileAltIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineClockEightIcon from 'LuxKit/Icons/line/LineClockEightIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineFileTimesAltIcon from 'LuxKit/Icons/line/LineFileTimesAltIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineShieldPlusIcon from 'LuxKit/Icons/line/LineShieldPlusIcon'
import { SUPPORT_DEFAULT_PHONE_DISPLAY } from 'constants/support'

interface FAQ {
  icon: React.ReactElement
  question: string
  answer: string
}

export const HARDCODED_SUPPLIERS: Array<{ name: string, image: App.Image }> = [
  { name: 'Avis', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/avis.svg' } },
  { name: 'Hertz', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/hertz.svg' } },
  { name: 'Europcar', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/europcar.svg' } },
  { name: 'Sixt', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/sixt.svg' } },
  { name: 'Thrifty', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/thrifty.svg' } },
  { name: 'Budget', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/budget.svg' } },
  { name: 'Alamo', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/alamo.svg' } },
  { name: 'Enterprise', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/enterprise.svg' } },
]

export const TRANSPORTATION_CODE = {
  AIRPLANE: 14,
}

export function getCarHireFAQ(isCarHireInsuranceEnabled: boolean, intl: IntlShape): Array<FAQ> {
  const FAQ = [
    {
      icon: <LineFileAltIcon />,
      question: intl.formatMessage({
        id: '5EX1kmeWc-j47EQKBcyh',
        defaultMessage: 'What documents do I need to rent a car?',
        description: 'FAQ question about documents needed to rent a car in the car hire section',
      }),
      answer: intl.formatMessage({
        id: 'XyoUZFHfFl2HX_GDUxrt',
        defaultMessage: 'To rent a car, you will typically require:<ol><li><strong>Valid driver’s licence:</strong> Remember to carry it with you when picking up the rental car.</li><li><strong>Identification:</strong> A valid passport or national identification card to confirm your identity.</li><li><strong>Payment method:</strong> In your name with sufficient funds to cover the rental charges and any security deposit.</li></ol>',
        description: 'FAQ answer about documents needed to rent a car in the car hire section',
      }, {
        ol: chunks => `<ol>${chunks}</ol>`,
        li: chunks => `<li>${chunks}</li>`,
        strong: chunks => `<strong>${chunks}</strong>`,
      }),
    },
    {
      icon: <LineUserIcon />,
      question: intl.formatMessage({
        id: '9j547swkuZedkoLap8o6',
        defaultMessage: 'How old do I need to be to rent a car?',
        description: 'FAQ question about minimum age to rent a car in the car hire section',
      }),
      answer: intl.formatMessage({
        id: '16EOX5tD4lAuVhQprNum',
        defaultMessage: 'The minimum driver age for most rental companies is 25 years. Driver’s younger than 30 may incur a surcharge or be limited in the vehicle type they can select. Older drivers aged over 70 may require a medical and incur a surcharge. Make sure you enter your correct age, at the time of hire, when searching for a car so you can see the options available for you. All guests intending to drive must present a valid full national driving license, which has been held for at least three years. A current international driving license or translation must accompany driving licenses issued in a language other than English.',
        description: 'FAQ answer about minimum age requirements to rent a car',
      }),
    },
    {
      icon: <LineCarIcon />,
      question: intl.formatMessage({
        id: 'h5JMZH0heAOR2CiWYf79',
        defaultMessage: 'What should I look for when choosing a rental car?',
        description: 'FAQ question about choosing a rental car on the rental page',
      }),
      answer: intl.formatMessage({
        id: 'Hsf1PWj4OiizHGQ_PUbZ',
        defaultMessage: '- Passenger and baggage capacity: Ensure ample space for you, your passengers, and all your luggage by checking the passenger and baggage capacity. - Transmission: Choose between manual and automatic transmission to ensure you’re comfortable with the car’s driving system. - Mileage policy: Verify the mileage policy to understand if there are any limitations on the number of kilometers you can drive during the rental period. - Pick up and drop off location: Confirm the pick-up and drop-off location, especially for airport rentals, as you may need to utilize a free shuttle or terminal service. Find all this information in the offer and your booking confirmation.',
        description: 'FAQ answer about what to consider when choosing a rental car',
      }),
    },
    ...(isCarHireInsuranceEnabled ? [{
      icon: <LineShieldCheckIcon />,
      question: intl.formatMessage({
        id: 'tbwVO2MAFtB6tMdR1n8u',
        defaultMessage: 'Do I need insurance when renting a car?',
        description: 'FAQ question about car hire insurance in the insurance or protection selection',
      }),
      answer: intl.formatMessage({
        id: 'cCFhWj7o8bpTFA-BsDpV',
        defaultMessage: 'It is highly recommended to have insurance coverage when renting a car. Most rental companies offer insurance options, including Collision Damage Waiver (CDW) and Theft Protection, which provide financial protection in case of an accident, theft, or damage to the rental vehicle. It’s important to review the insurance terms and conditions prior to booking. Luxury Escapes offers an Enhanced Protection insurance option that covers up to 100% of the excess in case of an incident. This provides you with added peace of mind during your rental period.',
        description: 'FAQ answer about the importance of car hire insurance',
      }),
    }] : []),
    ...(isCarHireInsuranceEnabled ? [{
      icon: <LineShieldPlusIcon />,
      question: intl.formatMessage({
        id: 'm91LhZLE5Sax9kVM2Ops',
        defaultMessage: 'Can I purchase insurance through Luxury Escapes?',
        description: 'FAQ question about purchasing insurance through Luxury Escapes',
      }),
      answer: intl.formatMessage({
        id: 'WGU5A82i3J7Rhb869OC-',
        defaultMessage: 'Absolutely! Luxury Escapes offers an Enhanced Protection insurance option that covers up to 100% refundable excess in case of an incident. This provides you with added peace of mind during your rental period. Currently, Enhanced Protection coverage can be purchased as an add-on during your Car Hire checkout. Full coverage information can be found by clicking learn more on each policy on the checkout page.',
        description: 'FAQ answer about purchasing Enhanced Protection insurance through Luxury Escapes',
      }),
    }] : []),
    {
      icon: <LineExclamationTriangleIcon />,
      question: intl.formatMessage({
        id: '5lWy8y2toPX81ULepOWJ',
        defaultMessage: 'What should I do if I get into an accident in a rental car?',
        description: 'FAQ question about what to do in a rental car accident',
      }),
      answer: intl.formatMessage({
        id: '-0OHtTdsT5ET7di7weqZ',
        defaultMessage: 'If you find yourself in an accident while driving a rental car, follow these steps: 1. Ensure safety: Prioritise your safety and the safety of others involved. Move to a safe location if possible and contact emergency services if necessary. 2. Inform the rental company and police: Report the accident immediately using their designated emergency contact number. They will guide you on the necessary next steps. 3. Document the incident: Take photos of the accident scene, gather information from any involved parties or witnesses, and make note of any relevant details. This documentation may be required for insurance claims. Information you’ll likely need to collect includes registration and license number of both vehicles and drivers, address of the other driver and photos of the incident.',
        description: 'FAQ answer about steps to take after a rental car accident',
      }),
    },
    {
      icon: <LineClockEightIcon />,
      question: intl.formatMessage({
        id: 'YOQ9gapXZaPHVkKLYdeA',
        defaultMessage: 'What happens if I return the car late?',
        description: 'FAQ question about late return of rental car on the rental page',
      }),
      answer: intl.formatMessage({
        id: 'a94kTJCmMPLQ-mp__MZd',
        defaultMessage: 'Returning the rental car late may result in additional charges. Rental companies usually have specific policies regarding late returns, including fees charged per hour or additional full-day charges. It’s important to adhere to the agreed-upon return time to avoid any unexpected costs. If you anticipate being late, contact the rental company in advance to discuss possible solutions.',
        description: 'FAQ answer about consequences and advice for late car returns',
      }),
    },
    {
      icon: <LineFileTimesAltIcon />,
      question: intl.formatMessage({
        id: 'gGWMQC9K0Ome5p_NmZvi',
        defaultMessage: 'Can I cancel my rental reservation?',
        description: 'FAQ question about cancelling a rental car reservation',
      }),
      answer: intl.formatMessage({
        id: 'kfnAma9fgdxSYke0Jm1R',
        defaultMessage: 'Most reservations can be cancelled up to 48 hours prior to pick up. Your cancellation policy is clearly highlighted on your booking. Prior to purchase, review the terms and conditions of your specific reservation as they may vary depending on the rental company. To cancel your reservation, please contact our Customer Support team on {phoneNumber}.',
        description: 'FAQ answer about rental car cancellation policy in the car hire section',
      }, { phoneNumber: SUPPORT_DEFAULT_PHONE_DISPLAY }),
    },
  ]

  return FAQ
}
